import Breadcrumbs from 'core/components/common/Breadcrumbs';
import CenterLoader from 'core/components/common/CenterLoader';
import NotFound from 'core/components/common/NotFound';
import { useLinks } from 'core/components/linksContext';
import { useIdsFromUrlWithoutParse } from 'core/hooks/useIdsFromUrl';
import { container } from 'core/style';
import injectSheet, { createStyles } from 'core/style/injectSheet';
import { ITheme, WithStyles } from 'core/style/interfaces';
import gql from 'graphql-tag';
import React from 'react';
import { Helmet } from 'react-helmet';
import LastUpdate from 'site/components/high-low/LastUpdate';
import { useGetDividendDetailQuery } from 'site/graphql';
import InvestmentDisclaimer from '../InvestmentDisclaimer';
import DividendDetailCandlesChart from './DividendDetailCandlesChart';
import DividendDetailHistory from './DividendDetailHistory';
import DividendDetailRating from './DividendDetailRating';
import DividendDetailSecurity from './DividendDetailSecurity';

gql`
    query GetDividendDetail($symbol: String!) {
        dividend(symbol: $symbol) {
            lastUpdate
            historyErrorMessage
            price
            name
            target
            candles {
                time
                open
                close
                high
                low
            }
            history {
                period
                lastDayToBuy
                registryClose
                dividend
                paymentYield
                yield
                paid
                timeGap
                gapClosed
                gapDawn
            }
            security {
                secId
                name
                high
                highDate
                low
                lowDate
                current
                currentDate
                position
                amplitude
                highPercent
                lowPercent
                d1
                w1
                m1
                y1
                yTD
                moscowExchangesIndex
                moscowExchangeBlueChipIndex
            }
        }
    }
`;

type IDividendDetailProps = {} & WithStyles<typeof styles>;

const DividendDetail: React.FC<IDividendDetailProps> = ({ classes }) => {
    const { symbol } = useIdsFromUrlWithoutParse('symbol');
    const { data, loading } = useGetDividendDetailQuery({
        fetchPolicy: 'cache-and-network',
        variables: { symbol },
    });
    const name = data?.dividend?.name;
    const historyErrorMessage = data?.dividend?.historyErrorMessage;
    const links = useLinks();

    if (loading) {
        return <CenterLoader />;
    }

    // @see \src\site\components\product\public\productDetailFetchData.tsx
    if (!loading && (!data || !data.dividend)) {
        return <NotFound />;
    }

    return (
        <>
            <Helmet>
                <title>{`Акции ${name}: цена, дивиденды, оценка`}</title>
                <meta
                    name="description"
                    property="og:description"
                    content={`Информация об акциях ${name}: Минимумы и максимумы за год, оценка стоимости акции компании, а также дивидендная история`}
                ></meta>
                <meta name="keywords" content={`${name}, акции, дивиденды`}></meta>
            </Helmet>
            <div className={classes.root}>
                <Breadcrumbs
                    contrast={false}
                    items={[
                        { href: links.home.url(), title: 'Главная' },
                        { href: links.servicePage.url(), title: 'Сервисы инвестора' },
                        { href: links.dividendTable.url(), title: 'Таблица дивидендных акций' },
                        { title: name },
                    ]}
                />
                <h1 className={classes.title}>{name}</h1>
                <p className={classes.subtitle}>
                    Минимумы и максимумы за год, оценка возможной стоимости акции компании, а также дивидендная история
                </p>
                <LastUpdate lastUpdate={data.dividend.lastUpdate} className={classes.lastUpdate} />
                <DividendDetailSecurity security={data.dividend.security} />

                <DividendDetailCandlesChart candles={data.dividend.candles} className={classes.candles} />
                <a href="https://ru.tradingview.com/" className={classes.copyrightNotice}>
                    TradingView Lightweight Charts
                    <br /> Copyright © {new Date().getFullYear()} TradingView, Inc.
                </a>

                <h2 className={classes.title}>Оценка стоимости акций</h2>
                <DividendDetailRating dividend={data.dividend} />

                <h2 className={classes.title}>Дивидендная история</h2>
                {historyErrorMessage ? (
                    <p>{historyErrorMessage}</p>
                ) : (
                    <DividendDetailHistory history={data.dividend.history} />
                )}

                <InvestmentDisclaimer />
            </div>
        </>
    );
};

const styles = (theme: ITheme) =>
    createStyles({
        root: {
            ...container(theme),
            padding: '1rem 0',
        },
        title: {
            fontSize: 16,
            lineHeight: 18 + 'px',
            color: '#363636',
            marginTop: 2 + 'rem',
        },
        subtitle: {
            fontSize: 14,
            fontWeight: 'bold',
            lineHeight: 16 + 'px',
            color: theme.colors.textLight,
            marginBottom: 2 + 'rem',

            [theme.breakpoints.up('sm')]: {
                marginBottom: 50,
            },
        },
        candles: {
            marginTop: 1 + 'rem',
        },
        lastUpdate: {
            marginBottom: 50,
        },
        copyrightNotice: {
            fontSize: 11 + 'px',
            lineHeight: 14 + 'px',
            textAlign: 'right',
            color: theme.colors.textLight,
            display: 'flex',
            justifyContent: 'flex-end',
        },
    });

export default injectSheet(styles)(DividendDetail);
